/**
 * TemplatesPage — read-only listing of Proxmox templates per cluster
 * with inline VMID→role assignment.
 *
 * Theme: matches the rest of the app (dark zinc, cm-* utility classes,
 * shared Atoms — see AgentsPage / InfrastructurePage for the same look).
 */
import React, { useMemo, useState } from "react";
import useSWR, { mutate as globalMutate } from "swr";
import {
    Boxes,
    RefreshCw,
    Sparkles,
    AlertCircle,
    CheckCircle2,
    HardDrive,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api.js";
import { PageHeader, Spinner, Empty } from "@/components/Atoms";

const fetcher = (u) => api.get(u).then((r) => r.data);

// Build the assignment-dropdown options from the live nc_versions list
// + the static "debian13" base + the "unassigned" sentinel. Only
// `active` versions are offered for NEW assignments; EOL versions still
// appear if a template is already mapped to them (so the operator can
// see — and un-assign — them) but are not selectable for new templates.
function buildKindOptions(ncVersions, currentlyMapped) {
    const options = [
        { value: "",         label: "— unassigned —" },
        { value: "debian13", label: "debian13 (service VMs base)" },
        { value: "debian12", label: "debian12 (Talk signaling — janus SFU)" },
    ];
    const seen = new Set();
    const add = (v, eol) => {
        if (seen.has(v)) return;
        seen.add(v);
        const suffix = eol ? "  (eol)" : "";
        options.push({ value: `nc${v}-web`,    label: `nc${v}-web${suffix}` });
        options.push({ value: `nc${v}-worker`, label: `nc${v}-worker${suffix}` });
    };
    // First the active versions, sorted descending
    (ncVersions || [])
        .filter((x) => x.status === "active")
        .sort((a, b) => b.version - a.version)
        .forEach((x) => add(x.version, false));
    // Then EOL versions that still have a mapping (so operator can clean up)
    const eolWithMappings = (ncVersions || [])
        .filter((x) => x.status === "eol")
        .filter((x) =>
            (currentlyMapped || []).some(
                (m) => m === `nc${x.version}-web` || m === `nc${x.version}-worker`,
            ),
        );
    eolWithMappings.forEach((x) => add(x.version, true));
    return options;
}

export default function TemplatesPage() {
    const { data: clusters } = useSWR("/api/admin/infra/proxmox", fetcher);
    const { data: summary, mutate: refreshSummary } = useSWR(
        "/api/admin/infra/templates/summary",
        fetcher,
    );
    const { data: ncVersions, mutate: refreshNcVersions } = useSWR(
        "/api/admin/infra/nc-versions",
        fetcher,
    );

    const [clusterId, setClusterId] = useState(null);

    const activeClusterId = useMemo(() => {
        if (clusterId) return clusterId;
        if (clusters?.length) return clusters[0].id || clusters[0]._id;
        return null;
    }, [clusterId, clusters]);

    const activeCluster = useMemo(
        () => clusters?.find((c) => (c.id || c._id) === activeClusterId),
        [activeClusterId, clusters],
    );

    const {
        data: templates,
        mutate: refreshTemplates,
        isValidating,
        isLoading,
    } = useSWR(
        activeClusterId
            ? `/api/admin/infra/proxmox/${activeClusterId}/templates`
            : null,
        fetcher,
    );

    // Build kind-dropdown options from live nc_versions + current mappings
    const currentlyMapped = useMemo(
        () => (templates || []).map((t) => t.mapped_to).filter(Boolean),
        [templates],
    );
    const kindOptions = useMemo(
        () => buildKindOptions(ncVersions, currentlyMapped),
        [ncVersions, currentlyMapped],
    );

    const assignKind = async (vmid, newKind) => {
        if (!activeCluster) return;
        const map = { ...(activeCluster.template_map || {}) };
        for (const [k, v] of Object.entries(map)) {
            if (Number(v) === Number(vmid)) delete map[k];
        }
        if (newKind) map[newKind] = Number(vmid);

        try {
            await api.patch(
                `/api/admin/infra/proxmox/${activeClusterId}`,
                { template_map: map },
            );
            toast.success(
                newKind
                    ? `VMID ${vmid} → ${newKind}`
                    : `VMID ${vmid} unassigned`,
            );
            await Promise.all([
                refreshTemplates(),
                refreshSummary(),
                globalMutate("/api/admin/infra/proxmox"),
            ]);
        } catch (e) {
            toast.error(
                "Assignment failed: " +
                    (e?.response?.data?.detail || e.message),
            );
        }
    };

    // ── Prep-VM builder state — single-step form: version + password +
    // explicit VMIDs (auto-suggested from existing nc<N>-{web,worker}
    // templates so the operator usually just hits "Create"). Backend
    // clones the debian13 template twice (web + worker) ASYNCHRONOUSLY
    // via a job pattern (POST returns job_id in <100ms; UI polls the
    // job endpoint every 3s for live progress). This eliminates the
    // "click OK → nothing happens" UX where reverse proxies (Plesk,
    // nginx) closed the connection at 30-60s during the 90-300s clone.
    const [prepVersion, setPrepVersion] = useState("");
    const [prepPassword, setPrepPassword] = useState("");
    const [prepWebVmid, setPrepWebVmid] = useState("");
    const [prepWorkerVmid, setPrepWorkerVmid] = useState("");
    const [prepBusy, setPrepBusy] = useState(false);
    const [prepJobId, setPrepJobId] = useState(null);
    const [prepError, setPrepError] = useState(null);

    // Live polling of the active prep-vm job. SWR with a 3s refresh
    // interval re-renders the progress checklist as the worker thread
    // posts new stages. We stop polling automatically when the job
    // transitions to "done" or "error".
    const { data: prepJob } = useSWR(
        prepJobId && activeClusterId
            ? `/api/admin/infra/proxmox/${activeClusterId}/templates/prep-vms/jobs/${prepJobId}`
            : null,
        fetcher,
        {
            refreshInterval: (latest) => {
                if (!latest) return 3000;
                if (latest.status === "running") return 3000;
                return 0;  // stop polling once done/error
            },
        },
    );

    // When the job finishes, surface a toast + refresh templates.
    // Using a ref-style guard so the toast fires once.
    const lastReportedJobIdRef = React.useRef(null);
    React.useEffect(() => {
        if (!prepJob || prepJob.status === "running") return;
        if (lastReportedJobIdRef.current === prepJob.id) return;
        lastReportedJobIdRef.current = prepJob.id;
        setPrepBusy(false);
        if (prepJob.status === "done") {
            const vms = prepJob.result?.vms || [];
            toast.success(
                `Prep VMs created: ${vms.map((x) => `${x.name}#${x.vmid}`).join(", ")}`,
            );
            setPrepPassword("");
            setPrepWebVmid("");
            setPrepWorkerVmid("");
            refreshTemplates();
        } else if (prepJob.status === "error") {
            toast.error(
                "Prep-VM creation failed: " +
                (prepJob.error || "unknown error"),
            );
        }
        // intentionally NOT depending on refreshTemplates — its identity
        // changes on every render and would re-trigger this effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prepJob]);

    // Auto-suggest VMIDs whenever the version changes — uses the new
    // backend endpoint that walks every cluster's template_map and
    // returns max(nc*-web)+1 / max(nc*-worker)+1. The operator can edit
    // the suggestions before submitting.
    React.useEffect(() => {
        const v = parseInt(prepVersion, 10);
        if (!activeClusterId || !v || v < 10 || v > 99) return;
        let cancelled = false;
        api.get(
            `/api/admin/infra/proxmox/${activeClusterId}/templates/prep-vms/suggest-vmids`,
            { params: { version: v } },
        )
            .then((r) => {
                if (cancelled) return;
                if (!prepWebVmid) setPrepWebVmid(String(r.data.web_vmid));
                if (!prepWorkerVmid) setPrepWorkerVmid(String(r.data.worker_vmid));
            })
            .catch(() => {});  // suggestion is best-effort; ignore errors
        return () => { cancelled = true; };
        // intentionally NOT depending on prepWebVmid/prepWorkerVmid to
        // avoid an effect loop — we only seed defaults once per version.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prepVersion, activeClusterId]);

    const createPrepVms = async () => {
        if (!activeClusterId) {
            toast.error("No active cluster selected");
            return;
        }
        const v = parseInt(prepVersion, 10);
        if (!v || v < 10 || v > 99) {
            toast.error("Version must be a number between 10 and 99");
            return;
        }
        if (prepPassword && prepPassword.length < 4) {
            toast.error("Password must be at least 4 characters (or leave it empty for key-only SSH)");
            return;
        }
        const webId = prepWebVmid ? parseInt(prepWebVmid, 10) : null;
        const workerId = prepWorkerVmid ? parseInt(prepWorkerVmid, 10) : null;
        if ((prepWebVmid && (!Number.isFinite(webId) || webId < 100))
            || (prepWorkerVmid && (!Number.isFinite(workerId) || workerId < 100))) {
            toast.error("VMIDs must be integers ≥ 100");
            return;
        }
        if (webId && workerId && webId === workerId) {
            toast.error("Web and Worker VMIDs must differ");
            return;
        }
        if (!window.confirm(
            `Create prep VMs for Nextcloud ${v}?\n\n` +
            `• cm-nc${v}-web-prep    ${webId ? `(VMID ${webId})` : "(auto-VMID)"} — 2 CPU, 4 GB RAM, 30 GB disk\n` +
            `• cm-nc${v}-worker-prep ${workerId ? `(VMID ${workerId})` : "(auto-VMID)"} — 4 CPU, 8 GB RAM, 50 GB disk\n\n` +
            `Target node: ${activeCluster?.template_node || "<auto Control Plane>"}\n` +
            `Storage:     ${activeCluster?.storage || "<cluster default storage>"}\n` +
            `Bridge:      ${activeCluster?.public_bridge || "vmbr1"}\n\n` +
            `Both clone from the cluster's debian13 template (this takes 90-300s — ` +
            `you'll see live progress below).`
        )) return;
        setPrepBusy(true);
        setPrepJobId(null);
        setPrepError(null);
        lastReportedJobIdRef.current = null;
        try {
            const r = await api.post(
                `/api/admin/infra/proxmox/${activeClusterId}/templates/prep-vms`,
                {
                    version: v,
                    ...(prepPassword ? { root_password: prepPassword } : {}),
                    ...(webId ? { web_vmid: webId } : {}),
                    ...(workerId ? { worker_vmid: workerId } : {}),
                },
            );
            // Backend returned 202 with job_id in <100ms — UI now polls
            // the job endpoint for progress.
            setPrepJobId(r.data.job_id);
            toast.success(
                `Prep-VM job started — building on ${activeCluster?.template_node || "auto CP"}, ` +
                `IPs ${r.data.web_ip} + ${r.data.worker_ip}`,
            );
        } catch (e) {
            setPrepBusy(false);
            const detail = e?.response?.data?.detail || e.message || String(e);
            // Persist the error in a box (toasts vanish; operators were
            // missing the failure reason → "click OK, nothing happens").
            setPrepError(detail);
            toast.error("Prep-VM creation failed: " + detail);
        }
        // NB: prepBusy stays true while the job polls; cleared in the
        // useEffect above when the job transitions to done/error.
    };

    return (
        <div data-testid="templates-page">
            <PageHeader
                testid="templates-header"
                title={
                    <span className="flex items-center gap-3">
                        <Boxes size={22} className="text-cm-brand" />
                        Templates
                    </span>
                }
                subtitle="Proxmox templates per cluster with VMID role assignment."
                action={
                    <button
                        data-testid="templates-refresh-btn"
                        onClick={() => {
                            refreshTemplates();
                            refreshSummary();
                        }}
                        disabled={isValidating}
                        className="cm-btn-ghost text-xs flex items-center gap-1.5"
                    >
                        <RefreshCw
                            size={12}
                            className={isValidating ? "animate-spin" : ""}
                        />
                        Refresh
                    </button>
                }
            />

            <div className="p-4 sm:p-6 space-y-5">
                {/* Cluster picker */}
                <div
                    data-testid="cluster-picker"
                    className="flex flex-wrap items-center gap-3"
                >
                    <span className="cm-label">Cluster</span>
                    <select
                        data-testid="cluster-picker-select"
                        value={activeClusterId || ""}
                        onChange={(e) => setClusterId(e.target.value || null)}
                        className="cm-input text-xs min-w-[320px]"
                    >
                        {(clusters || []).map((c) => (
                            <option key={c.id || c._id} value={c.id || c._id}>
                                {c.name} · {c.api_url}
                            </option>
                        ))}
                    </select>
                </div>

                <NcReadinessBanner summary={summary} />

                {/* Prep-VM builder — one-click web+worker clone from
                    the cluster's debian13 base. Operator types the NC
                    major version + cloud-init password, backend does
                    the rest. */}
                <div className="cm-card p-4 border-zinc-700 space-y-3"
                     data-testid="prep-vms-panel">
                    <div className="flex items-center gap-2">
                        <Sparkles size={14} className="text-emerald-400" />
                        <div className="cm-h4 text-emerald-200">
                            Create Prep VMs
                        </div>
                    </div>
                    <p className="text-xs text-zinc-400 leading-relaxed">
                        Clones the cluster&apos;s <code className="font-mono text-zinc-300">debian13</code> template
                        into two prep VMs you can SSH into to install Nextcloud, then promote to templates.
                        <br />
                        <span className="font-mono text-zinc-500">
                            cm-nc&lt;N&gt;-web-prep    · 2 CPU · 4 GB · 30 GB
                        </span>
                        <br />
                        <span className="font-mono text-zinc-500">
                            cm-nc&lt;N&gt;-worker-prep · 4 CPU · 8 GB · 50 GB
                        </span>
                    </p>
                    <div className="flex flex-wrap items-end gap-3">
                        <label className="block">
                            <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">
                                NC Version
                            </span>
                            <input
                                type="number"
                                min="10"
                                max="99"
                                value={prepVersion}
                                onChange={(e) => setPrepVersion(e.target.value)}
                                placeholder="35"
                                data-testid="prep-vms-version-input"
                                className="cm-input w-24 font-mono"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">
                                Web VMID
                            </span>
                            <input
                                type="number"
                                min="100"
                                max="999999"
                                value={prepWebVmid}
                                onChange={(e) => setPrepWebVmid(e.target.value)}
                                placeholder="auto"
                                data-testid="prep-vms-web-vmid-input"
                                className="cm-input w-28 font-mono"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">
                                Worker VMID
                            </span>
                            <input
                                type="number"
                                min="100"
                                max="999999"
                                value={prepWorkerVmid}
                                onChange={(e) => setPrepWorkerVmid(e.target.value)}
                                placeholder="auto"
                                data-testid="prep-vms-worker-vmid-input"
                                className="cm-input w-28 font-mono"
                            />
                        </label>
                        <label className="block flex-1 min-w-[200px]">
                            <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">
                                cmuser password (optional — SSH key is auto-installed)
                            </span>
                            <input
                                type="password"
                                value={prepPassword}
                                onChange={(e) => setPrepPassword(e.target.value)}
                                placeholder="leave empty → key-only SSH (ssh cmuser@…)"
                                autoComplete="new-password"
                                data-testid="prep-vms-password-input"
                                className="cm-input w-full"
                            />
                        </label>
                        <button
                            onClick={createPrepVms}
                            disabled={prepBusy || !activeClusterId}
                            data-testid="prep-vms-create-btn"
                            className="cm-btn-primary text-xs px-3 py-2 flex items-center gap-1 disabled:opacity-50"
                        >
                            <Sparkles size={12} />
                            {prepBusy ? "Building…" : "Create prep VMs"}
                        </button>
                    </div>
                    {/* Resolved infra target — what the backend WILL use.
                        Reads directly from the active cluster row so the
                        operator can sanity-check before clicking. */}
                    {activeCluster && (
                        <div
                            data-testid="prep-vms-target-preview"
                            className="text-xs font-mono text-zinc-400 bg-zinc-950/60 border border-zinc-800 rounded p-2 leading-relaxed"
                        >
                            <span className="text-zinc-500">target node:   </span>
                            <span
                                data-testid="prep-vms-target-node"
                                className={activeCluster.template_node ? "text-emerald-300" : "text-amber-300"}
                            >
                                {activeCluster.template_node || "auto · first online Control Plane"}
                            </span>
                            <br />
                            <span className="text-zinc-500">storage:       </span>
                            <span
                                data-testid="prep-vms-target-storage"
                                className={(activeCluster.template_storage || activeCluster.snippet_storage) ? "text-emerald-300" : "text-red-300"}
                            >
                                {activeCluster.template_storage || activeCluster.snippet_storage || "⚠ none — set template_storage on cluster"}
                            </span>
                            <br />
                            <span className="text-zinc-500">bridge:        </span>
                            <span
                                data-testid="prep-vms-target-bridge"
                                className="text-emerald-300"
                            >
                                {activeCluster.public_bridge || "vmbr1"}
                            </span>
                        </div>
                    )}
                    {/* Persistent error box — POST-level failures (e.g.
                          missing debian13 template) don't create a job, so
                          the job panel below never renders. Without this the
                          operator only saw a transient toast → "clicked OK,
                          nothing happened". This box stays until the next try. */}
                    {prepError && (
                        <div
                            data-testid="prep-vms-post-error"
                            className="cm-card p-3 text-xs border-red-700 bg-red-950/20"
                        >
                            <div className="flex items-start justify-between gap-2 mb-1">
                                <span className="font-semibold text-red-300">✗ Prep-VM creation failed</span>
                                <button
                                    type="button"
                                    onClick={() => setPrepError(null)}
                                    className="text-[11px] text-zinc-500 hover:text-zinc-300 shrink-0"
                                    data-testid="prep-vms-post-error-dismiss"
                                >
                                    dismiss
                                </button>
                            </div>
                            <div className="text-xs text-red-200 font-mono whitespace-pre-wrap leading-relaxed">
                                {prepError}
                            </div>
                        </div>
                    )}
                    {/* ── Live job progress panel.
                          Renders as soon as POST /prep-vms returns 202 +
                          a job_id. Polls every 3s while status=running
                          and stops once status=done|error. The progress
                          array is a chronological list of stages with
                          ts/stage/detail/vmid — we render them as a
                          live checklist that grows over the 90-300s
                          build. */}
                    {prepJob && (
                        <div
                            data-testid="prep-vms-job-panel"
                            className={`cm-card p-3 text-xs ${
                                prepJob.status === "done"
                                    ? "border-emerald-700 bg-emerald-950/20"
                                    : prepJob.status === "error"
                                    ? "border-red-700 bg-red-950/20"
                                    : "border-blue-700 bg-blue-950/20"
                            }`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div className={`font-semibold ${
                                    prepJob.status === "done"
                                        ? "text-emerald-300"
                                        : prepJob.status === "error"
                                        ? "text-red-300"
                                        : "text-blue-300"
                                }`}>
                                    {prepJob.status === "running" && (
                                        <>⟳ Building prep VMs for nc{prepJob.version}…</>
                                    )}
                                    {prepJob.status === "done" && (
                                        <>✓ Prep VMs ready for nc{prepJob.version}</>
                                    )}
                                    {prepJob.status === "error" && (
                                        <>✗ Prep VM build failed (nc{prepJob.version})</>
                                    )}
                                </div>
                                <span
                                    data-testid="prep-vms-job-status"
                                    className="text-[11px] font-mono text-zinc-500"
                                >
                                    job {prepJob.id?.slice(0, 8)}
                                </span>
                            </div>

                            {/* Reserved IPs — visible from the first poll
                                so the operator can confirm allocation
                                before the long clone finishes. */}
                            <div className="text-xs font-mono text-zinc-400 mb-2 leading-relaxed">
                                <span className="text-zinc-500">reserved IPs: </span>
                                <span className="text-amber-300">{prepJob.web_ip}</span>
                                <span className="text-zinc-600"> (web)</span>
                                <span className="text-zinc-600"> · </span>
                                <span className="text-amber-300">{prepJob.worker_ip}</span>
                                <span className="text-zinc-600"> (worker)</span>
                                {prepJob.web_vmid && (
                                    <>
                                        <br />
                                        <span className="text-zinc-500">VMIDs: </span>
                                        <span>{prepJob.web_vmid} / {prepJob.worker_vmid}</span>
                                    </>
                                )}
                            </div>

                            {/* Progress checklist. */}
                            <div
                                data-testid="prep-vms-job-progress"
                                className="space-y-0.5 font-mono text-[11px] max-h-48 overflow-y-auto pr-2"
                            >
                                {(prepJob.progress || []).map((p, i) => (
                                    <div
                                        key={i}
                                        className="flex gap-2 leading-snug"
                                    >
                                        <span className="text-zinc-600 shrink-0">
                                            {p.ts ? new Date(p.ts).toLocaleTimeString() : "—"}
                                        </span>
                                        <span className={
                                            p.stage?.includes("failed")
                                                ? "text-red-300 font-semibold shrink-0"
                                                : p.stage?.includes("done")
                                                ? "text-emerald-300 shrink-0"
                                                : "text-blue-300 shrink-0"
                                        }>
                                            {p.stage}
                                        </span>
                                        <span className="text-zinc-300">
                                            {p.detail}
                                        </span>
                                    </div>
                                ))}
                                {prepJob.status === "running"
                                    && (prepJob.progress || []).length === 0 && (
                                    <div className="text-zinc-500 italic">
                                        Waiting for worker thread…
                                    </div>
                                )}
                            </div>

                            {/* Error message (status=error). */}
                            {prepJob.status === "error" && prepJob.error && (
                                <div
                                    data-testid="prep-vms-job-error"
                                    className="mt-2 p-2 bg-red-950/40 border border-red-800/60 rounded text-xs text-red-200 font-mono whitespace-pre-wrap"
                                >
                                    {prepJob.error}
                                </div>
                            )}

                            {/* Success summary (status=done). */}
                            {prepJob.status === "done" && prepJob.result?.vms && (
                                <div className="mt-2">
                                    <ul className="font-mono text-zinc-300 space-y-0.5 text-xs">
                                        {prepJob.result.vms.map((v) => (
                                            <li
                                                key={v.vmid}
                                                data-testid={`prep-vms-result-row-${v.kind}`}
                                            >
                                                {v.kind.padEnd(7)} · VMID {v.vmid} · {v.name} · {v.node} · {v.cpu}c/{Math.round(v.ram_mb/1024)}g/{v.disk_gb}G · ip=<strong className="text-emerald-300">{v.ip}/{v.subnet_prefix}</strong> · gw={v.gateway || "—"} · user=<strong>{v.ciuser}</strong>
                                                {v.start_warning && (
                                                    <span className="ml-2 text-amber-300" title={v.start_warning}>
                                                        ⚠ start failed
                                                    </span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-2 text-zinc-400 text-xs">
                                        SSH into the prep VMs at the assigned IPs, install Nextcloud,
                                        then <code>qm template &lt;vmid&gt;</code> each VM. The IP
                                        reservation auto-releases once the VMID becomes a template.
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <NcVersionsManager
                    ncVersions={ncVersions}
                    onChange={() => {
                        refreshNcVersions();
                        refreshSummary();
                    }}
                />

                {/* Templates table */}
                {isLoading || !templates ? (
                    <Spinner />
                ) : templates.length === 0 ? (
                    <Empty
                        testid="templates-empty"
                        title="No templates on this cluster"
                        hint="Build a VM, prepare it, then `qm template <vmid>`. Refresh this page to see it appear."
                    />
                ) : (
                    <div className="cm-card overflow-hidden">
                        <div className="cm-table-wrap">
                            <table className="cm-table" data-testid="templates-list">
                                <thead>
                                    <tr>
                                        <th>VMID</th>
                                        <th>Name</th>
                                        <th>Node</th>
                                        <th className="text-right">Disk</th>
                                        <th className="text-right">RAM</th>
                                        <th className="text-right">CPU</th>
                                        <th>Assigned to</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {templates.map((t) => (
                                        <tr
                                            key={t.vmid}
                                            data-testid={`template-row-${t.vmid}`}
                                            className="hover:bg-zinc-900/50"
                                        >
                                            <td className="cm-td font-mono text-xs text-zinc-200">
                                                {t.vmid}
                                            </td>
                                            <td className="cm-td text-zinc-200">
                                                {t.name}
                                            </td>
                                            <td className="cm-td text-xs text-zinc-400">
                                                {t.node || "—"}
                                            </td>
                                            <td className="cm-td text-right text-xs text-zinc-300">
                                                {t.disk_gb ? `${t.disk_gb} GB` : "—"}
                                            </td>
                                            <td className="cm-td text-right text-xs text-zinc-300">
                                                {t.memory_mb ? `${t.memory_mb} MB` : "—"}
                                            </td>
                                            <td className="cm-td text-right text-xs text-zinc-300">
                                                {t.cores ?? "—"}
                                            </td>
                                            <td className="cm-td">
                                                <select
                                                    data-testid={`template-assign-${t.vmid}`}
                                                    value={t.mapped_to || ""}
                                                    onChange={(e) =>
                                                        assignKind(t.vmid, e.target.value)
                                                    }
                                                    className="cm-input text-xs min-w-[180px]"
                                                >
                                                    {kindOptions.map((k) => (
                                                        <option key={k.value} value={k.value}>
                                                            {k.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// --- NC readiness banner --------------------------------------------------
function NcReadinessBanner({ summary }) {
    if (!summary) return null;
    const versions = summary.nc_versions || [];

    if (versions.length === 0) {
        return (
            <div
                data-testid="nc-readiness-empty"
                className="cm-card p-4 border-amber-700/40 bg-amber-950/30
                           flex items-start gap-3"
            >
                <AlertCircle size={18} className="text-amber-400 mt-0.5 shrink-0" />
                <div>
                    <div className="text-sm font-medium text-amber-200">
                        No Nextcloud versions ready for deploy
                    </div>
                    <div className="text-xs text-amber-300/70 mt-1">
                        Assign at least one <code className="cm-code">ncXX-web</code>{" "}
                        AND one <code className="cm-code">ncXX-worker</code> below to
                        enable Cloud deployment.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="nc-readiness-banner"
            className="cm-card p-4 border-[#CCFF00]/30 bg-cm-cyan/5"
        >
            <div className="flex items-start gap-3">
                <Sparkles size={18} className="text-cm-brand mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-100">
                        Nextcloud version coverage
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        {versions.map((v) => (
                            <span
                                key={v.version}
                                data-testid={`nc-version-pill-${v.version}`}
                                title={
                                    v.ready
                                        ? `web=${v.web_vmid}  worker=${v.worker_vmid}`
                                        : `missing: ${(v.missing || []).join(", ")}`
                                }
                                className={
                                    v.ready
                                        ? "text-xs px-2 py-0.5 rounded-sm font-mono border border-emerald-600/40 bg-emerald-950/40 text-emerald-300"
                                        : "text-xs px-2 py-0.5 rounded-sm font-mono border border-amber-600/40 bg-amber-950/40 text-amber-300"
                                }
                            >
                                {v.ready ? (
                                    <CheckCircle2
                                        size={10}
                                        className="inline mr-1 -mt-px"
                                    />
                                ) : (
                                    <AlertCircle
                                        size={10}
                                        className="inline mr-1 -mt-px"
                                    />
                                )}
                                NC {v.version}
                            </span>
                        ))}
                    </div>
                    {summary.latest_ready && (
                        <div className="text-xs text-zinc-400 mt-2">
                            Default for new Clouds:{" "}
                            <span className="font-mono text-zinc-200">
                                NC {summary.latest_ready}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// --- NC versions catalogue manager ---------------------------------------
//
// Add / EOL / Delete NC major versions. The list directly drives the
// role-assignment dropdown above (active versions are selectable, EOL
// versions remain visible if a template is already mapped to them).
function NcVersionsManager({ ncVersions, onChange }) {
    const [newVersion, setNewVersion] = React.useState("");
    const [busy, setBusy] = React.useState(false);

    const add = async () => {
        const v = parseInt(newVersion, 10);
        if (!Number.isFinite(v) || v < 20 || v > 99) {
            toast.error("Version must be an integer between 20 and 99.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/api/admin/infra/nc-versions", {
                version: v,
                status: "active",
            });
            toast.success(`NC ${v} added (active)`);
            setNewVersion("");
            onChange?.();
        } catch (e) {
            toast.error(
                "Add failed: " + (e?.response?.data?.detail || e.message),
            );
        } finally {
            setBusy(false);
        }
    };

    const toggle = async (v, currentStatus) => {
        const next = currentStatus === "active" ? "eol" : "active";
        try {
            await api.patch(`/api/admin/infra/nc-versions/${v}`, { status: next });
            toast.success(`NC ${v} → ${next}`);
            onChange?.();
        } catch (e) {
            toast.error(
                "Status change failed: " +
                    (e?.response?.data?.detail || e.message),
            );
        }
    };

    const remove = async (v) => {
        if (!window.confirm(`Remove NC ${v} from the catalogue? Existing template assignments to this version remain in the DB but disappear from the dropdown.`)) {
            return;
        }
        try {
            await api.delete(`/api/admin/infra/nc-versions/${v}`);
            toast.success(`NC ${v} removed`);
            onChange?.();
        } catch (e) {
            toast.error(
                "Delete failed: " + (e?.response?.data?.detail || e.message),
            );
        }
    };

    if (!ncVersions) return null;

    return (
        <div
            data-testid="nc-versions-manager"
            className="cm-card p-4"
        >
            <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                    <div className="text-sm font-medium text-zinc-100">
                        Nextcloud versions catalogue
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                        Active versions show up in the role dropdown. EOL
                        versions hide from new deploys but existing Clouds
                        keep running.
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <input
                        data-testid="nc-version-new-input"
                        type="number"
                        min="20"
                        max="99"
                        placeholder="e.g. 35"
                        value={newVersion}
                        onChange={(e) => setNewVersion(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && add()}
                        className="cm-input text-xs w-20"
                    />
                    <button
                        data-testid="nc-version-add-btn"
                        onClick={add}
                        disabled={busy || !newVersion}
                        className="cm-btn text-xs disabled:opacity-50"
                    >
                        Add NC version
                    </button>
                </div>
            </div>

            {ncVersions.length === 0 ? (
                <div className="text-xs text-zinc-500 italic py-2">
                    No NC versions registered yet — add one above.
                </div>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {ncVersions.map((v) => (
                        <div
                            key={v.version}
                            data-testid={`nc-version-${v.version}`}
                            className={
                                "flex items-center gap-1 px-2 py-1 rounded border text-xs font-mono " +
                                (v.status === "active"
                                    ? "border-emerald-600/40 bg-emerald-950/30 text-emerald-200"
                                    : "border-zinc-700/60 bg-zinc-900/60 text-zinc-400")
                            }
                        >
                            <span>NC {v.version}</span>
                            <span className="text-[11px] opacity-70 uppercase tracking-wider">
                                {v.status}
                            </span>
                            <button
                                data-testid={`nc-version-${v.version}-toggle`}
                                title={
                                    v.status === "active"
                                        ? "Mark as EOL (hide from new deploys)"
                                        : "Re-activate"
                                }
                                onClick={() => toggle(v.version, v.status)}
                                className="ml-1 text-[11px] underline opacity-70 hover:opacity-100"
                            >
                                {v.status === "active" ? "→ eol" : "→ active"}
                            </button>
                            <button
                                data-testid={`nc-version-${v.version}-delete`}
                                title="Remove from catalogue"
                                onClick={() => remove(v.version)}
                                className="text-[11px] opacity-50 hover:opacity-100 hover:text-red-400 ml-1"
                            >
                                ✕
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

